import React, { useEffect, useState, useCallback } from "react";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getAccessToken } from "../../utils/auth";
import { getClientEmail } from "../../utils/getClientEmail";
import {
  getEmployeeIdFromToken,
  parseEmployeeIdFromAdminDetails,
} from "../../utils/tokenClaims";

const LeaveApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const resolveEmployeeId = useCallback(async () => {
    const token = getAccessToken();
    let employeeId = getEmployeeIdFromToken(token);
    if (employeeId) return employeeId;

    const email = getClientEmail();
    if (!email) return "";

    try {
      const res = await api.get(
        `getemail-admin-details/${encodeURIComponent(email)}`
      );
      return parseEmployeeIdFromAdminDetails(res.data);
    } catch {
      return "";
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      try {
        const response = await api.get("/leave/my-applications");
        setApplications(response.data?.applications || []);
        return;
      } catch (err) {
        const message =
          err.response?.data?.error || err.response?.data?.message || "";
        const missingEmployeeId = message
          .toLowerCase()
          .includes("employee id missing");

        if (!missingEmployeeId) {
          throw err;
        }

        const employeeId = await resolveEmployeeId();
        if (!employeeId) {
          throw new Error(
            "Employee ID not found. Please contact HR to update your profile, then log in again."
          );
        }

        const fallback = await api.get("/leave/data-get", {
          params: { employeeId },
        });
        setApplications(fallback.data?.leaves || []);
      }
    } catch (err) {
      setApplications([]);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load leave applications."
      );
    } finally {
      setLoading(false);
    }
  }, [resolveEmployeeId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return {
          backgroundColor: "#e6f7ee",
          color: "#10b981",
          borderColor: "#10b981",
        };
      case "rejected":
        return {
          backgroundColor: "#fef2f2",
          color: "#ef4444",
          borderColor: "#ef4444",
        };
      case "pending":
        return {
          backgroundColor: "#eff6ff",
          color: "#3b82f6",
          borderColor: "#3b82f6",
        };
      default:
        return {
          backgroundColor: "#f3f4f6",
          color: "#6b7280",
          borderColor: "#d1d5db",
        };
    }
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 24 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          My Leave Applications
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and track your leave requests and status
        </p>
      </div>

      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          marginBottom: 28,
        }}
      >
        <button
          onClick={fetchApplications}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(12px)",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: "10px 20px",
            color: "#475569",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 16h5v5" />
          </svg>
          Refresh
        </button>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "40vh",
              gap: 16,
            }}
          >
            <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
              Loading your applications...
            </span>
          </div>
        )}

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
              maxWidth: 1200,
              margin: "0 auto 24px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{ color: "#dc2626", margin: 0, fontWeight: 500 }}>{error}</p>
          </div>
        )}

        {!loading && applications.length === 0 && (
          <div
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: 16,
              padding: "48px 32px",
              textAlign: "center",
              border: "1px solid #e2e8f0",
              maxWidth: 520,
              margin: "0 auto",
              boxShadow: "0 4px 24px rgba(0, 0, 0, 0.04)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ margin: "0 auto 20px", display: "block" }}
            >
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <h3
              style={{
                fontSize: "1.15rem",
                fontWeight: 600,
                color: "#334155",
                margin: "0 0 8px",
              }}
            >
              No leave applications found
            </h3>
            <p style={{ color: "#64748b", maxWidth: 400, margin: "0 auto", fontSize: "0.95rem" }}>
              You haven't submitted any leave applications yet. Click the button
              below to request time off.
            </p>
            <button
              style={{
                marginTop: 24,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: 10,
                padding: "12px 24px",
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
              }}
              onClick={() => navigate("/Applyleave")}
            >
              Request Leave
            </button>
          </div>
        )}

        {!loading && applications.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {applications.map((app) => (
              <div
                key={app._id}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(12px)",
                  borderRadius: 16,
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04)",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    borderBottom: "1px solid #e2e8f0",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 16,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: "#334155",
                        margin: 0,
                      }}
                    >
                      {app.leaveType}
                    </h3>
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: 20,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        ...getStatusStyle(app.status),
                      }}
                    >
                      {app.status}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#64748b", margin: "0 0 4px", fontWeight: 500 }}>
                        Duration
                      </p>
                      <p style={{ fontSize: "0.9rem", fontWeight: 500, color: "#334155", margin: 0 }}>
                        {app.leaveDuration || "-"}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#64748b", margin: "0 0 4px", fontWeight: 500 }}>
                        Date(s)
                      </p>
                      <p style={{ fontSize: "0.9rem", fontWeight: 500, color: "#334155", margin: 0 }}>
                        {app.leaveDuration === "morethanoneDay"
                          ? `${formatDate(app.fromDate)} - ${formatDate(app.toDate)}`
                          : app.date
                          ? formatDate(app.date)
                          : "-"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p style={{ fontSize: "0.75rem", color: "#64748b", margin: "0 0 4px", fontWeight: 500 }}>
                      Reason
                    </p>
                    <p style={{ fontSize: "0.9rem", color: "#334155", margin: 0 }}>
                      {app.reason || "No reason provided"}
                    </p>
                  </div>
                </div>

                {app.rejectionMessage && (
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      padding: "16px 20px",
                      borderTop: "1px solid #fecaca",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dc2626"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <div>
                        <p style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: 600, margin: "0 0 4px" }}>
                          Rejection Message
                        </p>
                        <p style={{ fontSize: "0.9rem", color: "#991b1b", margin: 0 }}>
                          {app.rejectionMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveApplications;
